import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useColumnOrderSave } from "../../core/common/useColumnOrderSave";
import CollapseHeader from "../../core/common/collapse-header";
import Table from "../../core/common/dataTable/index";
import { fetchTodosByUserId, Todo } from "../../api/todo";
import { useSelector } from "react-redux";
import { RootState } from "../../core/data/redux/store";
import { all_routes } from "../router/all_routes";
import PageLoadProgressBar from "../../core/data/json/pageLoadProgressBar";
import { toast } from "react-toastify";
import { highlightMatch } from "../../core/common/highlightmatch";
import CountUp from "react-countup";
import KpiCard from "../../core/common/KPIcard";
import TodoModal from "../../core/modals/todoModal";
import { Dropdown, Menu, Button, Checkbox } from "antd";
import { DownOutlined, FunnelPlotFilled } from "@ant-design/icons";
import Modal from "bootstrap/js/dist/modal";
import ScrollToTop from "../../core/common/ScrollToTop";
import ModalDeleteEffect from "../../core/common/ModalDeleteEffect"; 
import Tip from "../../core/common/tooltip";
import AnimatedBadge from "../../core/common/AnimatedBadge";

const TodoComponent = () => {
  const userId = useSelector((state: RootState) => state.auth.user_id);
  const userRole = useSelector((state: RootState) => state.auth.role);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const route = all_routes;

  const [sortOption, setSortOption] = useState<string>("created_at_desc");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [priorityFilter, setPriorityFilter] = useState<string>("All");
  const [viewTab, setViewTab] = useState<string>("inbox");

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      index: true,
      title: true,
      description: true,
      priority: true,
      status: true,
      assigned_by: true,
      assigned_to: true,
      created_at: true,
      actions: true,
    }
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [orderedData, setOrderedData] = useState<Todo[]>([]);
  const defaultColumnOrder = [
    "index",
    "title",
    "description",
    "priority",
    "status",
    "assigned_by",
    "assigned_to",
    "created_at",
    "actions",
  ];

  const [columnKeys, setColumnKeys] = useState<string[]>(() => {
    const saved = localStorage.getItem("todoColumnKeys");
    return saved ? JSON.parse(saved) : defaultColumnOrder;
  });

  const { SaveModal } = useColumnOrderSave({
    columnKeys,
    localStorageKey: "todoColumnKeys",
    defaultOrder: defaultColumnOrder,
  });

  // KPI Counts
  const totalCount = useMemo(() => todos.length, [todos]);
  const pendingCount = useMemo(
    () => todos.filter((t) => t.status === "Pending").length,
    [todos]
  );
  const completedCount = useMemo(
    () => todos.filter((t) => t.status === "Completed").length,
    [todos]
  );
  const highPriorityCount = useMemo(
    () => todos.filter((t) => t.priority === "High").length,
    [todos]
  );

  // Fetch Todos
  const loadTodos = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await fetchTodosByUserId(userId);
      setTodos(data);
    } catch {
      toast.error("Failed to load todos");
    }
    setLoading(false);
  };

  useEffect(() => {
    const styleId = "animated-badge-keyframes";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes shine {
          0% {
            transform: skewX(-20deg) translateX(-150%);
          }
          50% {
             transform: skewX(-20deg) translateX(150%);
          }
          100% {
            transform: skewX(-20deg) translateX(150%);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("note-units");
    const cleanup2 = makeModalDraggable("edit_calls");
    const cleanup3 = makeModalDraggable("delete-note-units");
    const cleanup4 = makeModalDraggable("view-note-units");
    const cleanup5 = makeModalDraggable("import_excel_modal");
    const cleanup6 = makeModalDraggable("user_report_modal");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
      cleanup6?.();
    };
  }, []);

  useEffect(() => {
    loadTodos();
  }, [userId]);

  // Refresh after CRUD
  const refreshTodos = () => loadTodos();

  // Filters
  const filteredTodos = useMemo(() => {
    return todos.filter((todo) => {
      if (viewTab === "done" && todo.status !== "Completed") return false;
      if (viewTab === "important" && todo.priority !== "High") return false;

      const matchesSearch =
        todo.title.toLowerCase().includes(searchText.toLowerCase()) ||
        todo.description.toLowerCase().includes(searchText.toLowerCase());

      const matchesStatus =
        statusFilter === "All" || todo.status === statusFilter;
      const matchesPriority =
        priorityFilter === "All" || todo.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [todos, searchText, statusFilter, priorityFilter, viewTab]);

  // Sorting
  const sortedTodos = useMemo(() => {
    const priorityOrder: Record<string, number> = {
      High: 3,
      Medium: 2,
      Low: 1,
    };
    const sorted = [...filteredTodos];

    switch (sortOption) {
      case "created_at_asc":
        return sorted.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case "created_at_desc":
        return sorted.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case "priority_high":
        return sorted.sort(
          (a, b) => priorityOrder[b.priority] - priorityOrder[a.priority]
        );
      case "priority_low":
        return sorted.sort(
          (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
        );
      default:
        return sorted;
    }
  }, [filteredTodos, sortOption]);

  const handleRowOrderChange = (newOrdered: Todo[]) => {
            setOrderedData(newOrdered);
            // OPTIONAL: persist to backend
            // await reorderLeadSources(newOrdered.map(r => r.LeadSource_id));
          };

  const columns = [
    {
      title: "S.No",
      key: "index",
      render: (_: any, __: any, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: (a: Todo, b: Todo) => a.title.localeCompare(b.title),
      render: (text: string) => highlightMatch(text, searchText),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      sorter: (a: Todo, b: Todo) => a.description.localeCompare(b.description),
      render: (text: string) => highlightMatch(text, searchText),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      sorter: (a: Todo, b: Todo) => a.priority.localeCompare(b.priority),
      render: (_: any, record: Todo) => {
  const priority = record.priority || "Unknown";

  const priorityMap: Record<
    string,
    { icon: string; className: string; label: string }
  > = {
    High: {
      icon: "bi bi-bar-chart-fill",
      className: "bg-danger",
      label: "High",
    },
    Medium: {
      icon: "bi bi-bar-chart",
      className: "bg-success",
      label: "Medium",
    },
    Low: {
      icon: "bi bi-dash-lg",
      className: "bg-warning",
      label: "Low",
    },
  };

  const { icon, className, label } = priorityMap[priority] || {
    icon: "bi bi-question-circle",
    className: "bg-secondary",
    label: priority.replace(/_/g, " "),
  };

  return (
    <AnimatedBadge
      status={label}
      icon={icon}
      className={className}
    />
  );
},

    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a: Todo, b: Todo) => a.status.localeCompare(b.status),
      render: (_: any, record: Todo) => {
  const status = record.status || "Unknown";

  const statusMap: Record<
    string,
    { icon: string; className: string; label: string }
  > = {
    Completed: {
      icon: "bi bi-check-circle",
      className: "bg-success",
      label: "Completed",
    },
    Pending: {
      icon: "bi bi-hourglass-split",
      className: "bg-info",
      label: "Pending",
    },
    "In Progress": {
      icon: "bi bi-arrow-repeat",
      className: "bg-warning",
      label: "In Progress",
    },
    Onhold: {
      icon: "bi bi-pause-circle",
      className: "bg-danger",
      label: "On Hold",
    },
  };

  const { icon, className, label } = statusMap[status] || {
    icon: "bi bi-question-circle",
    className: "bg-secondary",
    label: status.replace(/_/g, " "),
  };

  return (
    <AnimatedBadge
      status={label}
      icon={icon}
      className={className}
    />
  );
},

    },
    {
      title: "Assigned By",
      dataIndex: "assigned_by_user",
      key: "assigned_by",
      render: (user: any) => user?.name || "N/A",
    },
    {
      title: "Assigned To",
      dataIndex: "user",
      key: "assigned_to",
      render: (user: any) => user?.name || "N/A",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: Todo, b: Todo) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) =>
        new Date(date).toLocaleString("en-US", {
          dateStyle: "medium",
          timeStyle: "short",
        }),
    },
    {
      title: "Actions",
      key: "actions",
       fixed: "right",
      render: (_: any, record: Todo) => {
  const menu = (
    <Menu
      onClick={(e) => e.domEvent.stopPropagation()}
      style={{ minWidth: 160 }}
    >
      <Menu.Item
        key="edit"
        data-bs-toggle="modal"
        data-bs-target="#edit-note-units"
        onClick={() => {
          setSelectedTodo(record);
          // open edit modal here if needed
        }}
      >
        <i className="ti ti-edit text-blue me-2" />
        Edit
      </Menu.Item>

      <Menu.Item
        key="delete"
        data-bs-toggle="modal"
              data-bs-target="#delete-note-units"
        onClick={() => {
          setSelectedTodo(record);
          // open delete modal here if needed
        }}
      >
        <i className="ti ti-trash text-danger me-2" />
        Delete
      </Menu.Item>

      <Menu.Item
        key="view"
        data-bs-toggle="modal"
              data-bs-target="#view-note-units"
        onClick={() => {
          setSelectedTodo(record);
          // open view modal here if needed
        }}
      >
        <i className="ti ti-eye text-primary me-2" />
        View
      </Menu.Item>
    </Menu>
  );
  return (
    <Dropdown
      overlay={menu}
      trigger={["click"]}
      placement="bottomRight"
      onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
    >
      <Button
                      size="small"
                      type="text"
                      className="table-action-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="fa fa-ellipsis-v" />
                    </Button>
    </Dropdown>
  );
},

    },
  ];

  // Make modals draggable (same as LeadSource)
  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;

      const dragStart = (e: MouseEvent) => {
        if (
          e.target instanceof HTMLElement &&
          e.target.closest("button, input, .btn-close")
        )
          return;
        if (modalHeader.contains(e.target as Node)) {
          isDragging = true;
          initialX = e.clientX - currentX;
          initialY = e.clientY - currentY;
          modalHeader.style.cursor = "move";
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;
        modalDialog.style.left = `${currentX}px`;
        modalDialog.style.top = `${currentY}px`;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
        }
      };

      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      return () => {
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    const cleanup1 = makeModalDraggable("note-units");
    const cleanup2 = makeModalDraggable("edit-note-units");
    const cleanup3 = makeModalDraggable("delete-note-units");
    const cleanup4 = makeModalDraggable("view-note-units");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
    };
  }, []);

  const now = new Date();
const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

const thisMonthCount = useMemo(
  () =>
    todos.filter(
      (t) => new Date(t.created_at) >= firstDayOfMonth
    ).length,
  [todos]
);

const total = totalCount;

const thisMonthPercent =
  total > 0 ? ((thisMonthCount / total) * 100).toFixed(1) : "0";

const pendingPercent =
  total > 0 ? ((pendingCount / total) * 100).toFixed(1) : "0";

const completedPercent =
  total > 0 ? ((completedCount / total) * 100).toFixed(1) : "0";


  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header mb-0">
            <div className="row align-items-center">
              <div className="col-sm-8">
                <h4 className="page-title">
                  <i className="bi bi-card-checklist text-primary me-2" />
                  Todo List
                  <span className="count-title ms-2">
                    <CountUp end={todos.length} duration={2.5} separator="," />
                  </span>
                </h4>
              </div>
              <div className="col-sm-4 text-sm-end">
                <div className="head-icons">
                  <CollapseHeader />
                </div>
              </div>
            </div>
          </div>
           {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="page-header-breadcrumb mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link 
                      to={userRole === "admin" ? route.dealsDashboard : route.leadsDashboard}
                      style={{ 
                        color: "#9595b5", 
                        textDecoration: "none",
                        transition: "color 0.2s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "#E41F07"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "#9595b5"}
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    ToDO
                  </li>
                </ol>
              </nav>
              {/* /Breadcrumb */}

          {/* KPI Cards */}
          <div className="row g-3 mb-4">
  {/* TOTAL TASKS */}
  <div className="col-12 col-sm-6 col-lg-3">
    <Tip text={`Total Tasks: ${totalCount} (100%)`} placement="top">
      <div className="kpi-card-wrapper h-100">
        <KpiCard
          title="Tasks"
          subtitle="Total"
          value={totalCount}
          icon="ti ti-list-check"
          color="primary"
          tooltip="Total number of tasks"
        />
      </div>
    </Tip>
  </div>

  {/* THIS MONTH */}
  <div className="col-12 col-sm-6 col-lg-3">
    <Tip
      text={`Added This Month: ${thisMonthCount} (${thisMonthPercent}%)`}
      placement="top"
    >
      <div className="kpi-card-wrapper h-100">
        <KpiCard
          title="Tasks"
          subtitle="This Month"
          value={thisMonthCount}
          icon="ti ti-calendar"
          color="warning"
          tooltip="Tasks created in the current month"
          percentage={thisMonthPercent}
          percentageColor={
            parseFloat(thisMonthPercent) > 0 ? "success" : "danger"
          }
          percentageIcon={
            parseFloat(thisMonthPercent) > 0
              ? "ti ti-arrow-up"
              : "ti ti-arrow-down"
          }
        />
      </div>
    </Tip>
  </div>

  {/* PENDING */}
  <div className="col-12 col-sm-6 col-lg-3">
    <Tip
      text={`Pending: ${pendingCount} (${pendingPercent}%)`}
      placement="top"
    >
      <div className="kpi-card-wrapper h-100">
        <KpiCard
          title="Tasks"
          subtitle="Pending"
          value={pendingCount}
          icon="ti ti-hourglass"
          color="warning"
          tooltip="Tasks that are still pending"
          percentage={pendingPercent}
          percentageColor="warning"
          percentageIcon="ti ti-minus"
        />
      </div>
    </Tip>
  </div>

  {/* COMPLETED */}
  <div className="col-12 col-sm-6 col-lg-3">
    <Tip
      text={`Completed: ${completedCount} (${completedPercent}%)`}
      placement="top"
    >
      <div className="kpi-card-wrapper h-100">
        <KpiCard
          title="Tasks"
          subtitle="Completed"
          value={completedCount}
          icon="ti ti-check"
          color="success"
          tooltip="Tasks that are completed"
          percentage={completedPercent}
          percentageColor="success"
          percentageIcon="ti ti-arrow-up"
        />
      </div>
    </Tip>
  </div>
</div>


          <div className="row">
            {/* Sidebar Filters - Clean, Responsive, No Collapse */}
            <div className="col-xl-3 col-md-12">
              <div className="card stickybar">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Filters</h5>
                </div>
                <div className="card-body p-3">
                  <div className="mb-3">
                    <h6 className="fw-semibold mb-2">View</h6>
                    <div className="list-group list-group-flush">
                      <button
                        className={`list-group-item list-group-item-action ${
                          viewTab === "inbox" ? "active" : ""
                        }`}
                        onClick={() => {
                          setViewTab("inbox");
                          setStatusFilter("All");
                          setPriorityFilter("All");
                        }}
                      >
                        <i className="ti ti-inbox me-2" />
                        Inbox{" "}
                        <span className="badge bg-primary ms-2">
                          {todos.length}
                        </span>
                      </button>
                      <button
                        className={`list-group-item list-group-item-action ${
                          viewTab === "done" ? "active" : ""
                        }`}
                        onClick={() => {
                          setViewTab("done");
                          setStatusFilter("All");
                          setPriorityFilter("All");
                        }}
                      >
                        <i className="ti ti-check-circle me-2" />
                        Completed
                      </button>
                    </div>
                  </div>

                  <hr className="my-3" />

                  <div className="mb-3">
                    <h6 className="fw-semibold mb-2">Status</h6>
                    <div className="list-group list-group-flush">
                      {[
                        "All",
                        "Pending",
                        "In Progress",
                        "Completed",
                        "Onhold",
                      ].map((s) => (
                        <button
                          key={s}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            statusFilter === s ? "active" : ""
                          }`}
                          onClick={() => {
                            setViewTab("inbox");
                            setStatusFilter(s);
                          }}
                        >
                          {s}
                          <span className="badge bg-secondary">
                            {
                              todos.filter((t) => t.status === s || s === "All")
                                .length
                            }
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <hr className="my-3" />

                  <div className="mb-3">
                    <h6 className="fw-semibold mb-2">Priority</h6>
                    <div className="list-group list-group-flush">
                      {["All", "High", "Medium", "Low"].map((p) => (
                        <button
                          key={p}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            priorityFilter === p ? "active" : ""
                          }`}
                          onClick={() => {
                            setViewTab("inbox");
                            setPriorityFilter(p);
                          }}
                        >
                          {p}
                          <span className="badge bg-secondary">
                            {
                              todos.filter(
                                (t) => t.priority === p || p === "All"
                              ).length
                            }
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    className="btn btn-outline-secondary w-100 mt-3"
                    onClick={() => {
                      setViewTab("inbox");
                      setStatusFilter("All");
                      setPriorityFilter("All");
                      setSearchText("");
                    }}
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="col-xl-9">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="icon-form mb-3 mb-sm-0">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search tasks..."
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                        {searchText && (
                          <button
                            type="button"
                            className="btn-close position-absolute end-0 top-50 translate-middle-y me-3"
                            style={{ zIndex: 10, fontSize: "12px" }}
                            onClick={() => setSearchText("")}
                            aria-label="Clear search"
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 text-md-end">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        data-bs-toggle="modal"
                        data-bs-target="#note-units"
                        style={{
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <i className="feather-plus-circle me-2" style={{ position: "relative", zIndex: 2 }} />
                        <span style={{ position: "relative", zIndex: 2 }}>Add Task</span>
                        <span
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                            transform: "skewX(-20deg) translateX(-150%)",
                            animation: "shine 3s infinite",
                          }}
                        />
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Active Filters */}
                  {(statusFilter !== "All" ||
                    priorityFilter !== "All" ||
                    searchText ||
                    viewTab !== "inbox") && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {searchText && (
                        <div className="alert alert-primary py-2 mb-0">
                          <strong>Search:</strong> {searchText}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setSearchText("")}
                          />
                        </div>
                      )}
                      {statusFilter !== "All" && (
                        <div className="alert alert-info py-2 mb-0">
                          <strong>Status:</strong> {statusFilter}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setStatusFilter("All")}
                          />
                        </div>
                      )}
                      {priorityFilter !== "All" && (
                        <div className="alert alert-warning py-2 mb-0">
                          <strong>Priority:</strong> {priorityFilter}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setPriorityFilter("All")}
                          />
                        </div>
                      )}
                      {viewTab !== "inbox" && (
                        <div className="alert alert-secondary py-2 mb-0">
                          <strong>View:</strong>{" "}
                          {viewTab === "done" ? "Completed" : viewTab}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setViewTab("inbox")}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <Table
                    columns={columns
                      .filter(
                        (col) =>
                          visibleColumns[col.key as keyof typeof visibleColumns]
                      )
                      .sort(
                        (a, b) =>
                          columnKeys.indexOf(a.key as string) -
                          columnKeys.indexOf(b.key as string)
                      )}
                    dataSource={sortedTodos}
                    rowKey="todo_id"
                    className="custom-table"
                    emptyText="No ToDo found"
  emptyIcon="ti ti-clipboard-off"
                    loading={loading}
                    
                    enableRowSelection={false}
  draggable={true}  
recordLabel="ToDo"
enableColumnDrag={true}
                    pagination={{
                      current: currentPage,
                      pageSize,
                      pageSizeOptions: ["10", "20", "50", "100"],
                      showSizeChanger: true,
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size || 10);
                      },
                    }}
                    rowSelection={{
                                                type: "checkbox",
                                                selectedRowKeys: selectedRows,
                                                onChange: (selectedRowKeys: React.Key[]) => {
                                                  setSelectedRows(selectedRowKeys as string[]);
                                                },
                                              }}
                      onRowOrderChange={handleRowOrderChange}
                      onColumnOrderChange={(newCols: any[]) => {
                                              // Get the new order of non-sticky columns from the dragged columns
                                              const newOrderedKeys = newCols
                                                .filter((c) => !c.fixed) // Only non-sticky columns
                                                .map((c) => c.key || c.dataIndex)
                                                .filter(
                                                  (key) =>
                                                    key !== undefined && key !== null && key !== ""
                                                ); // Remove invalid keys
                    
                                              // Preserve columns that are not in newCols (hidden columns) by merging with existing columnKeys
                                              const existingKeys = columnKeys;
                                              const allNonStickyKeys = defaultColumnOrder; // All possible non-sticky column keys
                    
                                              // Create a new array that:
                                              // 1. Uses the new order for visible columns
                                              // 2. Preserves the order of hidden columns that aren't in newCols
                                              const preservedKeys = existingKeys.filter(
                                                (key) => !newOrderedKeys.includes(key)
                                              );
                                              const finalKeys = [
                                                ...newOrderedKeys,
                                                ...preservedKeys,
                                              ];
                    
                                              // Ensure all default columns are included (in case some are missing)
                                              const missingKeys = allNonStickyKeys.filter(
                                                (key) => !finalKeys.includes(key)
                                              );
                                              const completeKeys = [...finalKeys, ...missingKeys];
                    
                                              setColumnKeys(completeKeys);
                                              // Don't auto-save, let user decide on navigation/refresh
                                            }}
                    scroll={{ x: "max-content" }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
<ModalDeleteEffect />
      {/* Modals */}
      <TodoModal
        selectedTodo={selectedTodo}
        setSelectedTodo={setSelectedTodo}
        refreshTodos={refreshTodos}
      />
      <ScrollToTop />
      <SaveModal />
    </>
  );
};

export default TodoComponent;
